import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, Mail, Loader2, Bot } from "lucide-react";
import api from "../lib/api";

const SUPPORT_PHONE = "+31650825998";
const SUPPORT_EMAIL = "info@rvservicetool.nl";
const WELCOME = "Hi! I'm the RV Service Tool assistant. Ask me about reports, the parts book, scanning, logging in, or your subscription. How can I help?";

function sessionId() {
  let s = localStorage.getItem("arv_chat_session");
  if (!s) { s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`; localStorage.setItem("arv_chat_session", s); }
  return s;
}

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, loading]);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    api.get(`/chat/history/${sid.current}`)
      .then(({ data }) => setMessages(data.length ? data : [{ role: "assistant", content: WELCOME }]))
      .catch(() => setMessages([{ role: "assistant", content: WELCOME }]));
  }, [open, loaded]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessages((m) => [...m, { role: "assistant", content: typeof detail === "string" ? detail : `Sorry, I couldn't answer that. Please call ${SUPPORT_PHONE} or email ${SUPPORT_EMAIL}.` }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="support-chat-toggle"
        className="fixed bottom-5 right-5 z-[70] w-14 h-14 rounded-full bg-[#F26722] text-white shadow-xl flex items-center justify-center hover:bg-[#D95513] transition-colors"
        aria-label="Open support chat">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div data-testid="support-chat-panel"
          className="fixed bottom-24 right-5 z-[70] w-[calc(100vw-2.5rem)] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-[#E0E0E0] flex flex-col overflow-hidden">
          <div className="surface-dark px-4 py-3 flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 rounded-full bg-[#F26722]/20 flex items-center justify-center"><Bot size={18} className="text-[#F26722]" /></span>
            <div className="leading-tight"><p className="font-bold text-white text-sm">RV Support</p><p className="text-[11px] text-zinc-300">Usually answers instantly</p></div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-[#F5F5F5]" data-testid="support-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#F26722] text-white rounded-br-sm" : "bg-white border border-[#E0E0E0] text-[#2D3748] rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-[#E0E0E0] text-[#7A8FA6] px-3 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> typing…</div></div>}
            <div ref={endRef} />
          </div>

          <div className="shrink-0 border-t border-[#E0E0E0] bg-white">
            <div className="flex items-center gap-3 px-3 py-1.5 text-[11px] text-[#7A8FA6] border-b border-[#EEE]">
              <span className="font-semibold">Need a person?</span>
              <a href={`tel:${SUPPORT_PHONE}`} className="flex items-center gap-1 text-[#3D6080] font-semibold" data-testid="support-chat-phone"><Phone size={12} /> {SUPPORT_PHONE}</a>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-1 text-[#3D6080] font-semibold"><Mail size={12} /> Email</a>
            </div>
            <form onSubmit={send} className="flex items-center gap-2 p-2.5">
              <input data-testid="support-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…"
                className="flex-1 min-w-0 bg-[#F5F5F5] border border-[#E0E0E0] text-[#2D3748] rounded-xl px-3 py-2.5 text-sm focus:border-[#F5A623] focus:outline-none" />
              <button type="submit" disabled={loading || !input.trim()} data-testid="support-chat-send"
                className="shrink-0 w-10 h-10 rounded-xl bg-[#F26722] text-white flex items-center justify-center disabled:opacity-50 hover:bg-[#D95513] transition-colors">
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
